import { useMemo } from "react";
import { useSelector, shallowEqual } from "react-redux";
import { Constants } from "config/constants";
import { Roles_Permissions_Constants } from "config/roles-permissions";

export const COMPLIANCE_ALLOWED_ROLE_LABELS = Object.freeze([
  "Default Registrar",
  "Uploader",
  "Registrar - Reviewer",
  "Registrar - Approver",
  "Approver",
  "Reviewer",
]);

const rawAllowedRoleIdentifiers = [
  Roles_Permissions_Constants.DASHBOARD_ROLES.REGISTRAR,
  Roles_Permissions_Constants.DASHBOARD_ROLES.UPLOADER,
  Roles_Permissions_Constants.DASHBOARD_ROLES.APPROVER,
  Roles_Permissions_Constants.DASHBOARD_ROLES.REVIEWER,
  "REGISTRAR_REVIEWER",
  "REGISTRAR-REVIEWER",
  "REGISTRAR_REVIEW",
  "REGISTRAR-REVIEW",
  "REGISTRAR_APPROVER",
  "REGISTRAR-APPROVER",
  "REGISTRAR_APPROVAL",
  "REGISTRAR-APPROVAL",
  "DEFAULT_REGISTRAR",
  "REGISTRAR_DEFAULT",
  "DEFAULT-REGISTRAR",
  "REGISTRAR DEFAULT",
  "Registrar",
  ...COMPLIANCE_ALLOWED_ROLE_LABELS,
];

const registrarRoleIdentifiers = [
  Roles_Permissions_Constants.DASHBOARD_ROLES.REGISTRAR,
  "Registrar",
  "Default Registrar",
  "Registrar - Reviewer",
  "Registrar - Approver",
  "Registrar Reviewer",
  "Registrar Approver",
  "DEFAULT_REGISTRAR",
  "REGISTRAR_DEFAULT",
  "REGISTRAR-DEFAULT",
  "Registrar Default",
];

const uploaderRoleIdentifiers = [
  Roles_Permissions_Constants.DASHBOARD_ROLES.UPLOADER,
  "Uploader",
  "Document Uploader",
  "Employee Uploader",
  "Org Admin Uploader",
  "OrgAdmin Uploader",
];

const createTokens = (value) => {
  if (value === undefined || value === null) {
    return [];
  }
  const stringValue = String(value).trim();
  if (!stringValue) {
    return [];
  }
  const upper = stringValue.toUpperCase();
  return [
    upper,
    upper.replace(/\s+/g, ""),
    upper.replace(/[\s-]+/g, "_"),
    upper.replace(/[\s_-]+/g, ""),
  ];
};

const COMPLIANCE_ALLOWED_ROLE_TOKENS = rawAllowedRoleIdentifiers.reduce(
  (tokens, identifier) => {
    createTokens(identifier).forEach((token) => tokens.add(token));
    return tokens;
  },
  new Set()
);

const REGISTRAR_ROLE_TOKENS = registrarRoleIdentifiers.reduce(
  (tokens, identifier) => {
    createTokens(identifier).forEach((token) => tokens.add(token));
    return tokens;
  },
  new Set()
);

const UPLOADER_ROLE_TOKENS = uploaderRoleIdentifiers.reduce(
  (tokens, identifier) => {
    createTokens(identifier).forEach((token) => tokens.add(token));
    return tokens;
  },
  new Set()
);

const ADMIN_ROLE_TOKENS = new Set([
  "SUPERADMIN",
  "SUPER_ADMIN",
  "SUPER ADMIN",
  "ORGADMIN",
  "ORG_ADMIN",
  "ORG ADMIN",
  "SYSTEMADMIN",
  "SYSTEM_ADMIN",
  "SYSTEM ADMIN",
]);

const ROLE_KEYS_TO_CHECK = [
  "name",
  "displayName",
  "label",
  "title",
  "role",
  "roleName",
  "slug",
  "code",
  "type",
  "value",
];

const collectRoleTokens = (role, seen = new WeakSet()) => {
  if (role === undefined || role === null) {
    return [];
  }

  if (typeof role === "string" || typeof role === "number") {
    return createTokens(role);
  }

  if (Array.isArray(role)) {
    return role.flatMap((item) => collectRoleTokens(item, seen));
  }

  if (typeof role === "object") {
    if (seen.has(role)) {
      return [];
    }
    seen.add(role);

    const tokens = [];
    ROLE_KEYS_TO_CHECK.forEach((key) => {
      if (role[key] !== undefined) {
        tokens.push(...collectRoleTokens(role[key], seen));
      }
    });
    return tokens;
  }

  return [];
};

const aggregateRoles = (sources) => {
  const aggregated = [];

  Object.values(sources).forEach((value) => {
    if (value === undefined || value === null) {
      return;
    }

    if (Array.isArray(value)) {
      aggregated.push(...value);
      return;
    }

    aggregated.push(value);
  });

  return aggregated;
};

const mapOrganizationRoles = (organizations = []) =>
  organizations.flatMap((organization) =>
    [
      organization?.role,
      organization?.roles,
      organization?.dashboardRoles,
      organization?.roleAssignments,
    ].filter(Boolean)
  );

const useComplianceAccess = () => {
  const roleSources = useSelector(
    (state) => ({
      organizationRoles: state.organizationsSlice?.dashboardRoles ?? [],
      companyRoles: state.companyDashboardSlice?.dashboardRoles ?? [],
      authDashboardRoles: state.authSlice?.dashboardRoles ?? [],
      authCurrentUserDashboardRoles:
        state.authSlice?.currentUser?.dashboardRoles ?? [],
      authCurrentUserRoles: state.authSlice?.currentUser?.roles ?? [],
      authCurrentUserRole: state.authSlice?.currentUser?.role ?? null,
      authCurrentUserRoleAssignments:
        state.authSlice?.currentUser?.roleAssignments ?? [],
      authCurrentUserOrganizations:
        state.authSlice?.currentUser?.organizations ?? [],
      userDashboardRoles: state.userSlice?.dashboardRoles ?? [],
      userRoles: state.userSlice?.roles ?? [],
      userRole: state.userSlice?.role ?? null,
    }),
    shallowEqual
  );

  const { currentUserId, authStatus } = useSelector(
    (state) => ({
      currentUserId: state.authSlice?.currentUser?._id ?? null,
      authStatus: state.authSlice?.getUserStatus?.status ?? "idle",
    }),
    shallowEqual
  );

  const augmentedRoleSources = useMemo(
    () => ({
      ...roleSources,
      authCurrentUserOrganizations: mapOrganizationRoles(
        roleSources.authCurrentUserOrganizations
      ),
    }),
    [roleSources]
  );

  const aggregatedRoles = useMemo(
    () => aggregateRoles(augmentedRoleSources),
    [augmentedRoleSources]
  );

  const aggregatedRoleTokens = useMemo(
    () =>
      aggregatedRoles
        .flatMap((role) => collectRoleTokens(role))
        .map((token) => token.toUpperCase()),
    [aggregatedRoles]
  );

  const numericRoleCandidates = useMemo(() => {
    const directCandidates = [
      roleSources.authCurrentUserRole,
      roleSources.userRole,
    ];

    const aggregatedCandidates = aggregatedRoles.filter(
      (role) => typeof role === "number" || typeof role === "string"
    );

    return [...directCandidates, ...aggregatedCandidates]
      .map((role) => {
        if (typeof role === "number") {
          return role;
        }
        if (typeof role === "string" && role.trim()) {
          const parsed = Number(role);
          if (!Number.isNaN(parsed)) {
            return parsed;
          }
        }
        return null;
      })
      .filter((value) => value !== null);
  }, [aggregatedRoles, roleSources.authCurrentUserRole, roleSources.userRole]);

  const adminNumericRoles = useMemo(
    () =>
      [
        Constants.ROLES?.SUPER_ADMIN,
        Constants.ROLES?.ORG_ADMIN,
        Number(Constants.COOKIE_ROLES?.SUPER_ADMIN),
        Number(Constants.COOKIE_ROLES?.ORG_ADMIN),
      ].filter((value) => typeof value === "number" && !Number.isNaN(value)),
    []
  );

  const registrarNumericRoles = useMemo(
    () =>
      [
        Constants.ROLES?.REGISTRAR,
        Constants.ROLES?.DEFAULT_REGISTRAR,
        Number(Constants.COOKIE_ROLES?.REGISTRAR),
        Number(Constants.COOKIE_ROLES?.DEFAULT_REGISTRAR),
      ].filter((value) => typeof value === "number" && !Number.isNaN(value)),
    []
  );

  const isOrgOrSuperAdmin = useMemo(() => {
    const hasAdminToken = aggregatedRoleTokens.some((token) =>
      ADMIN_ROLE_TOKENS.has(token)
    );
    if (hasAdminToken) {
      return true;
    }
    return numericRoleCandidates.some((candidate) =>
      adminNumericRoles.includes(candidate)
    );
  }, [aggregatedRoleTokens, adminNumericRoles, numericRoleCandidates]);

  const canAccessCompliance = useMemo(() => {
    if (!aggregatedRoles.length && !isOrgOrSuperAdmin) {
      return false;
    }

    if (isOrgOrSuperAdmin) {
      return true;
    }

    return aggregatedRoles.some((role) =>
      collectRoleTokens(role).some((token) =>
        COMPLIANCE_ALLOWED_ROLE_TOKENS.has(token)
      )
    );
  }, [aggregatedRoles, isOrgOrSuperAdmin]);

  const hasRegistrarRole = useMemo(() => {
    const hasRegistrarToken = aggregatedRoles.some((role) =>
      collectRoleTokens(role).some((token) => REGISTRAR_ROLE_TOKENS.has(token))
    );

    if (hasRegistrarToken) {
      return true;
    }

    if (!registrarNumericRoles.length) {
      return false;
    }

    return numericRoleCandidates.some((candidate) =>
      registrarNumericRoles.includes(candidate)
    );
  }, [aggregatedRoles, registrarNumericRoles, numericRoleCandidates]);

  const hasUploaderRole = useMemo(
    () =>
      aggregatedRoles.some((role) =>
        collectRoleTokens(role).some((token) => UPLOADER_ROLE_TOKENS.has(token))
      ),
    [aggregatedRoles]
  );

  const isAccessLoading =
    !currentUserId && (authStatus === "loading" || authStatus === "idle");

  return {
    canAccessCompliance,
    aggregatedRoles,
    isAccessLoading,
    isOrgOrSuperAdmin,
    hasRegistrarRole,
    hasUploaderRole,
  };
};

export default useComplianceAccess;
